/**
 * Created by piotr.pozniak@thebeaverhead.com on 20/09/2019.
 */

import React, {
  useCallback,
  useContext,
  useEffect,
  useId,
  useMemo,
  useRef,
  useState,
} from "react";
import classnames from "classnames";
import IntegrationContext from "../../strategies/IntegrationContext";
import { useTranslation } from "react-i18next";

/**
 * @param {Object} props
 * @param {String} props.header - visible label for the selector's trigger
 * @param {Array} props.options - list of filter options to choose from
 * @param {function} props.onChange - callback invoked with the chosen filter
 * @param {Object} props.currentSelection - currently selected filter
 * @param {Object} props.allOption - the "Any"/unset option
 * @param {String} props.allOption.kind
 * @param {String} [props.allOption.id]
 * @param {String} props.allOption.name
 */
const Selector = ({
  header,
  options,
  onChange,
  currentSelection,
  allOption,
}) => {
  const [showMenu, setShowMenu] = useState(false);
  const [activeIndex, setActiveIndex] = useState(0);
  const integrationContext = useContext(IntegrationContext);
  const { t } = useTranslation();

  const selectorId = "selector-" + useId().replace(/[^a-zA-Z0-9]/g, "");
  const listboxId = `${selectorId}-listbox`;

  const triggerRef = useRef();
  const optionRefs = useRef([]);

  const closeMenu = useCallback(() => {
    setShowMenu(false);
    triggerRef.current?.focus();
  }, []);

  const toggleMenu = useCallback(() => {
    setShowMenu((prev) => !prev);
  }, []);

  const onSelect = useCallback(
    (filter) => {
      setShowMenu(false);
      onChange(filter);
      triggerRef.current?.focus();
    },
    [onChange]
  );

  useEffect(() => {
    const handleClickOutsideSelector = (e) => {
      const container = document.getElementById(selectorId);

      if (container && !container.contains(e.target) && showMenu) {
        setShowMenu(false);
      }
    };

    document.addEventListener("click", handleClickOutsideSelector);

    return () => {
      document.removeEventListener("click", handleClickOutsideSelector);
    };
  }, [selectorId, showMenu]);

  const listOptions = useMemo(() => {
    const items = [{ id: null, label: t("Any"), node: allOption }];

    if (options.length) {
      options
        .filter((i) => {
          const fieldName = integrationContext.filterRenderName(i);
          return fieldName && fieldName.length;
        })
        .forEach((i) => {
          items.push({
            id: i.id,
            label: integrationContext.filterRenderName(i),
            node: i,
          });
        });
    }

    return items;
  }, [options, integrationContext, allOption, t]);

  // when the listbox opens, move keyboard focus to the currently selected option
  useEffect(() => {
    if (!showMenu) {
      return;
    }

    const selectedIndex = listOptions.findIndex(
      (i) => i.id === (currentSelection?.id || null)
    );
    const nextIndex = selectedIndex >= 0 ? selectedIndex : 0;

    setActiveIndex(nextIndex);
    optionRefs.current[nextIndex]?.focus();
  }, [showMenu]);

  const moveActive = useCallback((nextIndex) => {
    setActiveIndex(nextIndex);
    optionRefs.current[nextIndex]?.focus();
  }, []);

  const onListboxKeyDown = useCallback(
    (e) => {
      switch (e.key) {
        case "ArrowDown":
          e.preventDefault();
          moveActive((activeIndex + 1) % listOptions.length);
          break;
        case "ArrowUp":
          e.preventDefault();
          moveActive(
            (activeIndex - 1 + listOptions.length) % listOptions.length
          );
          break;
        case "Home":
          e.preventDefault();
          moveActive(0);
          break;
        case "End":
          e.preventDefault();
          moveActive(listOptions.length - 1);
          break;
        case "Enter":
        case " ":
          e.preventDefault();
          onSelect(listOptions[activeIndex].node);
          break;
        case "Escape":
          e.preventDefault();
          closeMenu();
          break;
        case "Tab":
          setShowMenu(false);
          break;
        default:
          break;
      }
    },
    [activeIndex, listOptions, moveActive, onSelect, closeMenu]
  );

  if (!options) {
    return null;
  }

  return (
    <div className="calendar-filters--selector" id={selectorId}>
      <div className="calendar-filters--options">
        <button
          type="button"
          ref={triggerRef}
          className={classnames(
            "dce-btn-reset",
            "calendar-filters--options_selected",
            { active: showMenu }
          )}
          onClick={toggleMenu}
          aria-haspopup="listbox"
          aria-expanded={showMenu}
          aria-controls={listboxId}
        >
          {header}
        </button>

        <div
          className={
            "calendar-filters--options_dropdown-container " +
            (showMenu ? "expanded" : "")
          }
        >
          <div
            className="calendar-filters--options_dropdown "
            role="listbox"
            id={listboxId}
            aria-label={header}
            onKeyDown={onListboxKeyDown}
          >
            {listOptions.map((item, idx) => {
              const isSelected = item.id === (currentSelection?.id || null);

              return (
                <div
                  key={item.id ?? "all"}
                  ref={(el) => (optionRefs.current[idx] = el)}
                  role="option"
                  aria-selected={isSelected}
                  tabIndex={idx === activeIndex ? 0 : -1}
                  onClick={() => onSelect(item.node)}
                  className={classnames(
                    "calendar-filters--options_dropdown-item",
                    { active: isSelected }
                  )}
                >
                  {isSelected ? (
                    <i className={"material-icons"} aria-hidden="true">
                      done
                    </i>
                  ) : null}
                  {item.label}
                </div>
              );
            })}
          </div>
        </div>
      </div>
    </div>
  );
};

export default Selector;
